import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye,
  Edit,
  MoreHorizontal,
  Clock,
  User,
  MessageCircle,
  Paperclip,
  ArrowRight
} from 'lucide-react';

import { apiHelper } from '@services/api';
import StatusBadge, { PriorityBadge } from '@components/common/StatusBadge';
import Button, { IconButton } from '@components/common/Button';
import Loading, { TableSkeleton } from '@components/common/Loading';
import DataTable from '@components/common/DataTable';

const TicketList = ({ searchParams, refreshKey }) => {
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [view, setView] = useState('table');

  const queryParams = {
    page: searchParams.get('page') || 1,
    limit: searchParams.get('limit') || 20,
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    assigned_to: searchParams.get('assigned_to') || '',
    module_id: searchParams.get('module_id') || '',
    branch_id: searchParams.get('branch_id') || '',
    created_from: searchParams.get('created_from') || '',
    created_to: searchParams.get('created_to') || ''
  };

  const {
    data: tickets,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['tickets', queryParams, refreshKey],
    async () => {
      const response = await apiHelper.get('/tickets');
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 30000
    }
  );

  const totalCount = tickets?.length || 0;
  const totalPages = Math.ceil(totalCount / queryParams.limit);

  const formatTimeAgo = timestamp => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 60000);

    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  const handleSelectTicket = id => {
    setSelectedTickets(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setSelectedTickets(
      selectedTickets.length === (tickets?.length || 0) ? [] : (tickets || []).map(t => t.id)
    );
  };

  if (error) {
    return (
      <div className='bg-white rounded-xl shadow-sm border p-8 text-center'>
        <div className='w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4'>
          <svg
            className='w-8 h-8 text-error-600'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>
        <h3 className='text-lg font-semibold text-gray-900 mb-2'>
          Failed to load tickets
        </h3>
        <p className='text-gray-600 mb-4'>
          There was an error loading the ticket data.
        </p>
        <Button onClick={refetch}>Try Again</Button>
      </div>
    );
  }

  if (isLoading) return <TableSkeleton rows={10} columns={6} />;

  const tableColumns = [
    // {
    //   key: 'select',
    //   label: (
    //     <input
    //       type='checkbox'
    //       checked={selectedTickets.length === (tickets?.length || 0) && (tickets?.length || 0) > 0}
    //       onChange={handleSelectAll}
    //       className='rounded border-gray-300 text-primary-600 focus:ring-primary-500'
    //     />
    //   ),
    //   render: ticket => (
    //     <input
    //       type='checkbox'
    //       checked={selectedTickets.includes(ticket.id)}
    //       onChange={() => handleSelectTicket(ticket.id)}
    //       className='rounded border-gray-300 text-primary-600 focus:ring-primary-500'
    //     />
    //   ),
    //   width: '40px'
    // },
    {
      key: 'ticket_number',
      label: 'Ticket',
      render: ticket => (
        <div className='flex flex-col'>
          <Link
            to={`/tickets/${ticket.id}`}
            className='font-medium text-primary-600 hover:text-primary-700 transition-colors'
          >
            {ticket.ticket_number}
          </Link>
          <span className='text-xs text-gray-500'>{formatTimeAgo(ticket.created_at)}</span>
        </div>
      )
    },
    {
      key: 'subject',
      label: 'Subject',
      render: ticket => (
        <div className='max-w-xs'>
          <p className='font-medium text-gray-900 truncate' title={ticket.subject}>
            {ticket.subject}
          </p>
          <div className='flex items-center gap-2 mt-1 text-xs text-gray-500'>
            <span>General</span>
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: ticket => (
        <div className='flex flex-col gap-1'>
          <StatusBadge status={ticket.status} size='sm' />
          <PriorityBadge priority={ticket.priority} size='sm' />
        </div>
      )
    },
    {
      key: 'assigned_to',
      label: 'Assignee',
      render: ticket => (
        <div className='flex items-center gap-2'>
          {ticket.assigned_to ? (
            <>
              <div className='w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center'>
                <User className='w-4 h-4 text-gray-600' />
              </div>
              <div>
                <p className='text-sm font-medium text-gray-900'>
                  User #{ticket.assigned_to}
                </p>
                <p className='text-xs text-gray-500'>
                  Assigned
                </p>
              </div>
            </>
          ) : (
            <span className='text-sm text-gray-500'>Unassigned</span>
          )}
        </div>
      )
    },
    {
      key: 'stats',
      label: 'Activity',
      render: ticket => (
        <div className='flex items-center gap-3 text-xs text-gray-500'>
          <div className='flex items-center gap-1'>
            <MessageCircle className='w-3 h-3' />
            <span>0</span>
          </div>
          <div className='flex items-center gap-1'>
            <Paperclip className='w-3 h-3' />
            <span>0</span>
          </div>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: ticket => (
        <div className='flex items-center gap-1'>
          <Link to={`/tickets/${ticket.id}`}>
            <IconButton variant='ghost' size='sm' tooltip='View ticket'>
              <Eye className='w-4 h-4' />
            </IconButton>
          </Link>
          <Link to={`/tickets/${ticket.id}/edit`}>
            <IconButton variant='ghost' size='sm' tooltip='Edit ticket'>
              <Edit className='w-4 h-4' />
            </IconButton>
          </Link>
          <IconButton variant='ghost' size='sm' tooltip='More options'>
            <MoreHorizontal className='w-4 h-4' />
          </IconButton>
        </div>
      )
    }
  ];

  return (
    <div className='space-y-6'>
      {/* Header Controls */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <p className='text-sm text-gray-600'>
            Showing {tickets?.length || 0} of {totalCount} tickets
          </p>
          {selectedTickets.length > 0 && (
            <div className='flex items-center gap-2'>
              <span className='text-sm font-medium text-gray-900'>
                {selectedTickets.length} selected
              </span>
              {/* <Button size='sm' variant='outline'>Bulk Actions</Button> */}
            </div>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant={view === 'table' ? 'primary' : 'ghost'}
            size='icon-sm'
            onClick={() => setView('table')}
          >
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 10h16M4 14h16M4 18h16' />
            </svg>
          </Button>
          <Button
            variant={view === 'grid' ? 'primary' : 'ghost'}
            size='icon-sm'
            onClick={() => setView('grid')}
          >
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' />
            </svg>
          </Button>
        </div>
      </div>

      {/* Table View */}
      {view === 'table' && (
        <DataTable
          data={tickets || []}
          columns={tableColumns}
          loading={isLoading}
          emptyMessage='No tickets found'
          pagination={{
            currentPage: parseInt(queryParams.page),
            totalPages,
            totalCount,
            pageSize: parseInt(queryParams.limit)
          }}
        />
      )}

      {/* Grid View */}
      {view === 'grid' && (
        <motion.div
          className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence>
            {(tickets || []).map((ticket, i) => (
              <motion.div
                key={ticket.id}
                className='bg-white border rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                onClick={() => window.open(`/tickets/${ticket.id}`, '_blank')}
              >
                <div className='flex items-center justify-between mb-4'>
                  <div className='flex items-center gap-2'>
                    <StatusBadge status={ticket.status} size='sm' />
                    <PriorityBadge priority={ticket.priority} size='sm' />
                  </div>
                  <span className='text-xs text-gray-500'>
                    {formatTimeAgo(ticket.created_at)}
                  </span>
                </div>
                <div className='mb-4'>
                  <h3 className='font-semibold text-gray-900 mb-1'>
                    {ticket.ticket_number}
                  </h3>
                  <p className='text-sm text-gray-600 line-clamp-2'>{ticket.subject}</p>
                </div>
                <div className='flex items-center justify-between text-sm text-gray-500'>
                  <div className='flex items-center gap-2'>
                    {ticket.assigned_to ? (
                      <>
                        <User className='w-4 h-4' />
                        <span>
                          User #{ticket.assigned_to}
                        </span>
                      </>
                    ) : (
                      <span>Unassigned</span>
                    )}
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='flex items-center gap-1'>
                      <MessageCircle className='w-3 h-3' />
                      <span>0</span>
                    </div>
                    <ArrowRight className='w-4 h-4' />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Empty State */}
      {(tickets?.length || 0) === 0 && !isLoading && (
        <motion.div
          className='bg-white rounded-xl shadow-sm border p-12 text-center'
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
            <Clock className='w-8 h-8 text-gray-400' />
          </div>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>
            No tickets found
          </h3>
          <p className='text-gray-500 mb-6'>
            No tickets match your current filters. Try adjusting your search.
          </p>
          <div className='flex justify-center gap-3'>
            <Button variant='outline' onClick={() => (window.location.href = '/tickets')}>
              Clear Filters
            </Button>
            <Link to='/tickets/create'>
              <Button variant='primary'>Create New Ticket</Button>
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default TicketList;
